/**
 * CuttingPlaneWidget
 * Interactive 3D widget for mesh cutter cutting planes
 * Features:
 * - Visual plane representation for each cutting plane
 * - Real-time clipping preview showing which part will be cut
 * - Interactive manipulation with TransformControls (translate + rotate)
 * - Support for multiple planes with selection
 * - Visibility toggle for each plane preview
 */
class CuttingPlaneWidget {
  constructor(viewer, filterManager) {
    this.viewer = viewer;
    this.filterManager = filterManager;
    this.renderer = viewer.engine;
    this.scene = viewer.engine.scene;
    this.camera = viewer.engine.camera;
    this.controls = viewer.engine.controls;

    // Widget state
    this.enabled = false;
    this.planeWidgets = new Map(); // Map of planeId -> { group, clippingPlane, visible, ... }
    this.activePlaneId = null;
    this.transformControls = null;
    this.basePlaneSize = 1.0;
    this.clippingPlanes = []; // THREE.Plane objects for visual preview only
    this.originalMeshVisible = true;

    // CSG cutting state (used only for "Apply Cut", not real-time)
    this.csgCutter = null;
    this.csgResultMesh = null;
    this.originalMeshes = new Map(); // Store original meshes for CSG operations
    this.useCSGCutting = false; // CSG only on "Apply Cut", not real-time (too slow)
    this.csgUpdatePending = false;
    this.csgUpdateDebounceTimer = null;

    // Bounded union clipper for real-time bounded union clipping
    // Uses THREE.js onBeforeCompile to achieve what native clipping planes can't:
    // Union of bounded regions (each cut is a bounded box/plane, multiple cuts union together)
    this.boundedClipper = null;
    this.useBoundedClipper = true; // Use shader-based bounded union clipping

    // Settings
    this.settings = {
      planeColor: 0xE24A4A,
      planeOpacity: 0.25,
      borderColor: 0x901A1A,
      borderOpacity: 0.7,
      selectedBorderColor: 0xFFD700,
      selectedBorderOpacity: 1.0
    };

    this.init();
  }

  init() {
    // TransformControls is now loaded statically in index.html
    this.setupEventListeners();
  }

  setupEventListeners() {
    const enableCheckbox = document.getElementById('meshCutterEnabled');
    if (enableCheckbox) {
      enableCheckbox.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.enable();
        } else {
          this.disable();
        }
      });

      if (enableCheckbox.checked) {
        setTimeout(() => this.enable(), 500);
      }
    }

    // Helper: Convert slider (0-100) to percentage value for U/V (maps to 0-200%)
    const sliderToPercent = (sliderVal) => sliderVal * 2;  // 0-100 slider -> 0-200%
    const percentToSlider = (percent) => Math.min(100, Math.max(0, percent / 2));  // Clamp to 0-100

    // Helper: For depth, slider 0-100 maps directly to 0-100%
    const depthSliderToPercent = (sliderVal) => sliderVal;
    const depthPercentToSlider = (percent) => Math.min(100, Math.max(0, percent));

    // Width (U) - slider and input bidirectional sync
    const scaleUSlider = document.getElementById('cutPlaneScaleU');
    const scaleUInput = document.getElementById('cutPlaneScaleUValue');

    if (scaleUSlider && scaleUInput) {
      // Slider changes -> update input and plane
      scaleUSlider.addEventListener('input', () => {
        const sliderVal = parseFloat(scaleUSlider.value);
        const percent = sliderToPercent(sliderVal);
        scaleUInput.value = percent;  // Full precision from slider
        this.updatePlaneScale('U', percent);
      });

      // Input changes -> update slider and plane (accepts decimals, unlimited range)
      scaleUInput.addEventListener('input', () => {
        let value = scaleUInput.value;
        // Don't update plane while typing incomplete decimals (ends with . or ,)
        // Just allow the input - plane will update when user finishes typing
        if (value === '' || value === '.' || value === ',' || value.endsWith('.') || value.endsWith(',')) {
          return;
        }
        // Support comma as decimal separator (convert to dot)
        value = value.replace(',', '.');
        const percent = parseFloat(value);
        if (!isNaN(percent) && percent > 0) {
          // Slider maxes out at 200%, but input can go higher
          scaleUSlider.value = percentToSlider(percent);
          this.updatePlaneScale('U', percent);
        }
      });

      // On blur, ensure valid (allow any positive decimal with full precision)
      scaleUInput.addEventListener('blur', () => {
        // Support comma as decimal separator and leading decimal (e.g., ",5" -> "0.5")
        let value = scaleUInput.value.replace(',', '.');
        if (value.startsWith('.')) value = '0' + value;
        const percent = parseFloat(value);
        if (isNaN(percent) || percent <= 0) {
          scaleUInput.value = 100;
        } else {
          // Normalize to use dot as decimal separator
          scaleUInput.value = percent;
        }
      });
    }

    // Height (V) - slider and input bidirectional sync
    const scaleVSlider = document.getElementById('cutPlaneScaleV');
    const scaleVInput = document.getElementById('cutPlaneScaleVValue');

    if (scaleVSlider && scaleVInput) {
      // Slider changes -> update input and plane
      scaleVSlider.addEventListener('input', () => {
        const sliderVal = parseFloat(scaleVSlider.value);
        const percent = sliderToPercent(sliderVal);
        scaleVInput.value = percent;  // Full precision from slider
        this.updatePlaneScale('V', percent);
      });

      // Input changes -> update slider and plane (accepts decimals, unlimited range)
      scaleVInput.addEventListener('input', () => {
        let value = scaleVInput.value;
        // Don't update plane while typing incomplete decimals (ends with . or ,)
        // Just allow the input - plane will update when user finishes typing
        if (value === '' || value === '.' || value === ',' || value.endsWith('.') || value.endsWith(',')) {
          return;
        }
        // Support comma as decimal separator (convert to dot)
        value = value.replace(',', '.');
        const percent = parseFloat(value);
        if (!isNaN(percent) && percent > 0) {
          // Slider maxes out at 200%, but input can go higher
          scaleVSlider.value = percentToSlider(percent);
          this.updatePlaneScale('V', percent);
        }
      });

      // On blur, ensure valid (allow any positive decimal with full precision)
      scaleVInput.addEventListener('blur', () => {
        // Support comma as decimal separator and leading decimal (e.g., ",5" -> "0.5")
        let value = scaleVInput.value.replace(',', '.');
        if (value.startsWith('.')) value = '0' + value;
        const percent = parseFloat(value);
        if (isNaN(percent) || percent <= 0) {
          scaleVInput.value = 100;
        } else {
          // Normalize to use dot as decimal separator
          scaleVInput.value = percent;
        }
      });
    }

    // Depth (D) - slider and input bidirectional sync
    const depthSlider = document.getElementById('cutPlaneDepth');
    const depthInput = document.getElementById('cutPlaneDepthValue');

    if (depthSlider && depthInput) {
      // Slider changes -> update input and plane
      depthSlider.addEventListener('input', () => {
        const sliderVal = parseFloat(depthSlider.value);
        const percent = depthSliderToPercent(sliderVal);
        depthInput.value = percent;  // Full precision from slider
        this.updatePlaneScale('D', percent);
      });

      // Input changes -> update slider and plane (accepts decimals, unlimited range)
      depthInput.addEventListener('input', () => {
        let value = depthInput.value;
        // Don't update plane while typing incomplete decimals (ends with . or ,)
        // Just allow the input - plane will update when user finishes typing
        if (value === '' || value === '.' || value === ',' || value.endsWith('.') || value.endsWith(',')) {
          return;
        }
        // Support comma as decimal separator (convert to dot)
        value = value.replace(',', '.');
        const percent = parseFloat(value);
        if (!isNaN(percent) && percent >= 0) {
          // Slider maxes out at 100%, but input can go higher
          depthSlider.value = depthPercentToSlider(percent);
          this.updatePlaneScale('D', percent);
        }
      });

      // On blur, ensure valid (allow any non-negative decimal with full precision)
      depthInput.addEventListener('blur', () => {
        // Support comma as decimal separator and leading decimal (e.g., ",5" -> "0.5")
        let value = depthInput.value.replace(',', '.');
        if (value.startsWith('.')) value = '0' + value;
        const percent = parseFloat(value);
        if (isNaN(percent) || percent < 0) {
          depthInput.value = 0;
        } else {
          // Normalize to use dot as decimal separator
          depthInput.value = percent;
        }
      });
    }
  }

  /**
   * Update plane scale (U, V, or D) for the selected plane
   */
  updatePlaneScale(dimension, percent) {
    if (!this.activePlaneId || !this.filterManager?.meshCutterPlanes) return;

    const plane = this.filterManager.meshCutterPlanes.find(p => p.id === this.activePlaneId);
    if (!plane) return;

    const ratio = percent / 100;  // Convert percentage to ratio

    switch (dimension) {
      case 'U':
        plane.scaleU = ratio;
        break;
      case 'V':
        plane.scaleV = ratio;
        break;
      case 'D':
        plane.depth = ratio;
        break;
    }

    this.rebuildPlaneWidget(this.activePlaneId);
  }

  /**
   * Rebuild a single plane widget
   */
  rebuildPlaneWidget(planeId) {
    const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === planeId);
    if (!plane) return;

    // Store current state
    const widget = this.planeWidgets.get(planeId);
    const wasVisible = widget?.visible ?? true;
    const wasClippingActive = widget?.clippingActive ?? true;
    const wasSelected = this.activePlaneId === planeId;

    // IMPORTANT: If this plane is selected, remove TransformControls FIRST
    // to avoid "object must be part of scene graph" errors
    if (wasSelected) {
      this.removeTransformControls();
      this.activePlaneId = null;
    }

    // Remove and recreate
    if (widget) {
      this.scene.remove(widget.group);
      widget.group.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(mat => mat.dispose());
          } else {
            child.material.dispose();
          }
        }
      });
      this.planeWidgets.delete(planeId);
    }

    // Recreate
    this.createPlaneWidget(plane);

    // Restore state
    const newWidget = this.planeWidgets.get(planeId);
    if (newWidget) {
      newWidget.visible = wasVisible;
      newWidget.group.visible = wasVisible;
      newWidget.clippingActive = wasClippingActive;
    }

    // Restore selection (re-attach TransformControls to new widget)
    if (wasSelected) {
      this.selectPlane(planeId);
    }

    this.updateClippingPlanes();
  }

  rebuildAllPlaneWidgets() {
    if (!this.filterManager?.meshCutterPlanes) return;

    const previousActiveId = this.activePlaneId;

    // Store visibility and clipping states
    const visibilityStates = new Map();
    const clippingStates = new Map();
    this.planeWidgets.forEach((widget, id) => {
      visibilityStates.set(id, widget.visible);
      clippingStates.set(id, widget.clippingActive);
    });

    this.removeAllPlaneWidgets();

    this.filterManager.meshCutterPlanes.forEach(plane => {
      if (plane.enabled) {
        this.createPlaneWidget(plane);
        // Restore visibility
        const wasVisible = visibilityStates.get(plane.id);
        if (wasVisible === false) {
          this.setPlaneWidgetVisible(plane.id, false);
        }
        // Restore clipping state
        const wasClippingActive = clippingStates.get(plane.id);
        if (wasClippingActive === false) {
          const widget = this.planeWidgets.get(plane.id);
          if (widget) widget.clippingActive = false;
        }
      }
    });

    if (previousActiveId && this.planeWidgets.has(previousActiveId)) {
      this.selectPlane(previousActiveId);
    }

    this.updateClippingPlanes();
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;
    console.log('[CuttingPlaneWidget] Enabled');

    // Enable clipping on renderer
    this.renderer.renderer.localClippingEnabled = true;

    // Initialize bounded union clipper if available
    if (this.useBoundedClipper && typeof BoundedUnionClipper !== 'undefined') {
      if (!this.boundedClipper) {
        this.boundedClipper = new BoundedUnionClipper();
      }
      this.boundedClipper.setEnabled(true);
    }

    this.syncWithPlaneData();
    this.updateClippingPlanes();
  }

  disable() {
    if (!this.enabled) return;
    this.enabled = false;
    console.log('[CuttingPlaneWidget] Disabled');

    // Cancel any pending CSG updates
    if (this.csgUpdateDebounceTimer) {
      clearTimeout(this.csgUpdateDebounceTimer);
      this.csgUpdateDebounceTimer = null;
    }

    // Restore original meshes from CSG cuts
    if (this.useCSGCutting && this.originalMeshes.size > 0) {
      this.restoreOriginalMeshes();
      this.originalMeshes.clear();
    }

    // Disable bounded clipper
    if (this.boundedClipper) {
      this.boundedClipper.setEnabled(false);
      this.boundedClipper.clearCuts();
    }

    // Remove clipping (legacy)
    this.clearClippingPlanes();

    this.removeAllPlaneWidgets();
    this.activePlaneId = null;

    // Show original mesh again
    this.showOriginalMesh();

    // Notify collaboration manager of the change (for state sync to followers)
    if (this.viewer?.viewLockManager) {
      this.viewer.viewLockManager.onVisualizationChange();
    }
  }

  /**
   * Update cutting - uses BoundedUnionClipper for true bounded union clipping
   * This provides real-time UNION of BOUNDED regions
   */
  updateClippingPlanes() {
    if (!this.enabled) {
      console.log('[CuttingPlaneWidget] updateClippingPlanes skipped - not enabled');
      return;
    }

    // Use CSG cutting for real geometry modification (on-demand only)
    if (this.useCSGCutting && typeof CSGCutter !== 'undefined') {
      this.scheduleCSGUpdate();
      return;
    }

    // Use bounded union clipper for proper bounded union clipping
    if (this.useBoundedClipper && this.boundedClipper) {
      this.updateBoundedClipping();
      return;
    }

    // Fallback to legacy clipping planes
    this.updateClippingPlanesLegacy();
  }

  /**
   * Update clipping using BoundedUnionClipper (shader-based bounded union)
   * This achieves what native THREE.js clipping cannot: union of bounded regions
   */
  updateBoundedClipping() {
    console.log('[CuttingPlaneWidget] updateBoundedClipping called, planeWidgets:', this.planeWidgets.size);

    // Clear existing cuts
    this.boundedClipper.clearCuts();

    // Collect all active cuts
    let cutCount = 0;
    this.planeWidgets.forEach((widget, planeId) => {
      console.log('[CuttingPlaneWidget] Checking widget:', planeId, 'clippingActive:', widget.clippingActive);
      if (!widget.clippingActive) return;

      const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === planeId);
      console.log('[CuttingPlaneWidget] Found plane:', plane?.id, 'enabled:', plane?.enabled);
      if (!plane || !plane.enabled) return;

      let normal = [plane.normal[0], plane.normal[1], plane.normal[2]];
      if (plane.invert) {
        normal = [-normal[0], -normal[1], -normal[2]];
      }

      // Add this cut to the bounded clipper
      this.boundedClipper.addCut({
        origin: plane.origin,
        normal: normal,
        width: widget.width,
        height: widget.height,
        depth: widget.depth || 0
      });
      cutCount++;
    });

    console.log(`[CuttingPlaneWidget] updateBoundedClipping: ${cutCount} cuts added`);

    // IMPORTANT: Set enabled state FIRST so uniforms have correct values when shaders compile
    const hasActiveCuts = this.boundedClipper.cuts.length > 0;
    this.boundedClipper.enabled = hasActiveCuts;

    // Apply bounded clipping to all mesh materials (triggers shader compilation)
    this.applyBoundedClippingToMeshes();

    // Update uniforms on materials that were already compiled
    this.boundedClipper.updateMaterials();

    // Render to trigger shader compilation for new materials
    this.renderer.renderer.render(this.scene, this.camera);

    // Update uniforms AGAIN after render to ensure newly compiled shaders get correct values
    this.boundedClipper.updateMaterials();

    // Notify collaboration manager of the change (for state sync to followers)
    if (this.viewer?.viewLockManager) {
      this.viewer.viewLockManager.onVisualizationChange();
    }
  }

  /**
   * Apply bounded union clipper to all mesh materials
   */
  applyBoundedClippingToMeshes() {
    const objects = this.renderer.objects || [];
    let appliedCount = 0;

    console.log('[CuttingPlaneWidget] applyBoundedClippingToMeshes, objects:', objects.length);

    objects.forEach(entry => {
      if (!entry.object) return;

      // Skip cutting plane widgets (they're just visualizations)
      if (entry.id === 'mesh-cutter' || entry.object.name?.startsWith('CuttingPlane_')) {
        return;
      }

      entry.object.traverse((child) => {
        // Skip objects that are part of cutting plane widgets
        if (child.name?.startsWith('CuttingPlane_') || child.parent?.name?.startsWith('CuttingPlane_')) {
          return;
        }

        if (child.isMesh && child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach(mat => {
            console.log('[CuttingPlaneWidget] Applying to material:', mat.type, 'uuid:', mat.uuid?.substring(0, 8));

            // Remove native clipping planes (we're using shader-based clipping)
            mat.clippingPlanes = null;
            mat.clipIntersection = false;

            // Apply bounded union clipper (it will skip basic materials internally)
            this.boundedClipper.applyToMaterial(mat);
            appliedCount++;
          });
        }
      });
    });

    console.log(`[CuttingPlaneWidget] Applied bounded clipper to ${appliedCount} materials`);
  }

  /**
   * Schedule a CSG update (debounced to avoid too frequent updates)
   */
  scheduleCSGUpdate() {
    if (this.csgUpdateDebounceTimer) {
      clearTimeout(this.csgUpdateDebounceTimer);
    }

    // Debounce CSG updates to 100ms
    this.csgUpdateDebounceTimer = setTimeout(() => {
      this.performCSGCut();
    }, 100);
  }

  /**
   * Perform CSG cutting operation
   */
  performCSGCut() {
    console.log('[CuttingPlaneWidget] Performing CSG cut...');

    // Get all meshes from the scene
    const meshesToCut = this.getMeshesToCut();
    if (meshesToCut.length === 0) {
      console.log('[CuttingPlaneWidget] No meshes to cut');
      return;
    }

    // Collect all active cuts
    const cuts = [];
    this.planeWidgets.forEach((widget, planeId) => {
      if (!widget.clippingActive) return;

      const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === planeId);
      if (!plane || !plane.enabled) return;

      let normal = [plane.normal[0], plane.normal[1], plane.normal[2]];
      if (plane.invert) {
        normal = [-normal[0], -normal[1], -normal[2]];
      }

      cuts.push({
        origin: plane.origin,
        normal: normal,
        width: widget.width,
        height: widget.height,
        depth: widget.depth || 0
      });
    });

    if (cuts.length === 0) {
      // No cuts - restore original meshes
      this.restoreOriginalMeshes();
      return;
    }

    console.log(`[CuttingPlaneWidget] Applying ${cuts.length} CSG cuts...`);

    // Process each mesh
    meshesToCut.forEach(meshEntry => {
      const mesh = meshEntry.mesh;
      const id = meshEntry.id;

      // Store original if not already stored
      if (!this.originalMeshes.has(id)) {
        this.originalMeshes.set(id, {
          geometry: mesh.geometry.clone(),
          material: mesh.material.clone ? mesh.material.clone() : mesh.material,
          visible: mesh.visible
        });
      }

      try {
        // Create CSG cutter for this mesh
        const cutter = new CSGCutter();

        // Create a temporary mesh with original geometry
        const originalData = this.originalMeshes.get(id);
        const tempMesh = new THREE.Mesh(originalData.geometry.clone(), mesh.material);
        tempMesh.matrixWorld.copy(mesh.matrixWorld);
        tempMesh.updateMatrixWorld(true);

        cutter.setMesh(tempMesh);

        // Add all cuts
        cuts.forEach(cut => {
          cutter.addBoxCut(cut);
        });

        // Apply cuts
        const resultMesh = cutter.applyCuts();

        if (resultMesh && resultMesh.geometry) {
          // Replace mesh geometry with cut result
          mesh.geometry.dispose();
          mesh.geometry = resultMesh.geometry;
          mesh.geometry.computeBoundingSphere();
          mesh.geometry.computeBoundingBox();
        }

      } catch (error) {
        console.error(`[CuttingPlaneWidget] CSG cut failed for mesh ${id}:`, error);
      }
    });

    this.renderer.renderer.render(this.scene, this.camera);
    console.log('[CuttingPlaneWidget] CSG cut complete');
  }

  /**
   * Get all meshes that should be cut
   */
  getMeshesToCut() {
    const meshes = [];
    const objects = this.renderer.objects || [];

    objects.forEach(entry => {
      if (!entry.object) return;

      entry.object.traverse((child) => {
        if (child.isMesh && child.geometry) {
          meshes.push({
            id: entry.id + '_' + child.uuid,
            mesh: child
          });
        }
      });
    });

    return meshes;
  }

  /**
   * Restore original meshes (before CSG cuts)
   */
  restoreOriginalMeshes() {
    const objects = this.renderer.objects || [];

    objects.forEach(entry => {
      if (!entry.object) return;

      entry.object.traverse((child) => {
        if (child.isMesh) {
          const id = entry.id + '_' + child.uuid;
          const originalData = this.originalMeshes.get(id);
          if (originalData) {
            child.geometry.dispose();
            child.geometry = originalData.geometry.clone();
          }
        }
      });
    });

    this.renderer.renderer.render(this.scene, this.camera);
    console.log('[CuttingPlaneWidget] Original meshes restored');
  }

  /**
   * Apply CSG cuts (called on "Apply Cut" button, not real-time)
   * This gives accurate union of bounded cuts
   */
  applyCSGCuts() {
    if (typeof CSGCutter === 'undefined') {
      console.warn('[CuttingPlaneWidget] CSG not available');
      return false;
    }

    this.performCSGCut();
    return true;
  }

  /**
   * Clipping planes method for real-time preview
   *
   * Single cut: BOUNDED (clipIntersection=true with 5-6 planes)
   * Multiple cuts: UNION but UNBOUNDED (clipIntersection=false with front planes only)
   *
   * This is a THREE.js limitation - can't do union of bounded regions with clipping planes.
   * For accurate bounded union cuts, use "Apply Cut" which uses CSG.
   */
  updateClippingPlanesLegacy() {
    this.clippingPlanes = [];

    // Count active cuts
    let activeCutCount = 0;
    this.planeWidgets.forEach((widget, planeId) => {
      if (widget.clippingActive) {
        const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === planeId);
        if (plane && plane.enabled) activeCutCount++;
      }
    });

    // Single cut = bounded, Multiple cuts = union (unbounded)
    const useBoundedMode = activeCutCount === 1;
    this.clipIntersectionMode = useBoundedMode;

    this.planeWidgets.forEach((widget, planeId) => {
      if (!widget.clippingActive) return;

      const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === planeId);
      if (!plane || !plane.enabled) return;

      const origin = new THREE.Vector3(plane.origin[0], plane.origin[1], plane.origin[2]);
      let normal = new THREE.Vector3(plane.normal[0], plane.normal[1], plane.normal[2]).normalize();

      if (plane.invert) {
        normal.negate();
      }

      const depthSize = widget.depth || 0;
      const halfWidth = widget.width / 2;
      const halfHeight = widget.height / 2;

      // Front cutting plane
      const frontPlane = new THREE.Plane(normal.clone(), -normal.dot(origin));
      widget.clippingPlane = frontPlane;
      this.clippingPlanes.push(frontPlane);

      if (useBoundedMode) {
        // BOUNDED MODE: Add all boundary planes
        const worldUp = Math.abs(normal.y) < 0.99 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
        const uAxis = new THREE.Vector3().crossVectors(worldUp, normal).normalize();
        const vAxis = new THREE.Vector3().crossVectors(normal, uAxis).normalize();

        // 4 boundary planes (normals pointing outward)
        const leftEdge = origin.clone().sub(uAxis.clone().multiplyScalar(halfWidth));
        this.clippingPlanes.push(new THREE.Plane(uAxis.clone().negate(), -uAxis.clone().negate().dot(leftEdge)));

        const rightEdge = origin.clone().add(uAxis.clone().multiplyScalar(halfWidth));
        this.clippingPlanes.push(new THREE.Plane(uAxis.clone(), -uAxis.dot(rightEdge)));

        const bottomEdge = origin.clone().sub(vAxis.clone().multiplyScalar(halfHeight));
        this.clippingPlanes.push(new THREE.Plane(vAxis.clone().negate(), -vAxis.clone().negate().dot(bottomEdge)));

        const topEdge = origin.clone().add(vAxis.clone().multiplyScalar(halfHeight));
        this.clippingPlanes.push(new THREE.Plane(vAxis.clone(), -vAxis.dot(topEdge)));

        // Back plane for volumes
        if (depthSize > 1.1920929e-7) {  // float32 epsilon
          const backOrigin = origin.clone().sub(normal.clone().multiplyScalar(depthSize));
          const backNormal = normal.clone().negate();
          this.clippingPlanes.push(new THREE.Plane(backNormal, -backNormal.dot(backOrigin)));
        }
      }
      // UNION MODE: Only front plane added (no boundaries)
    });

    this.applyClippingToMeshes();
    this.renderer.renderer.render(this.scene, this.camera);

    // Notify collaboration manager of the change (for state sync to followers)
    if (this.viewer?.viewLockManager) {
      this.viewer.viewLockManager.onVisualizationChange();
    }
  }

  /**
   * Apply clipping planes to all meshes (legacy fallback)
   */
  applyClippingToMeshes() {
    const useIntersection = this.clipIntersectionMode !== false;

    const objects = this.renderer.objects || [];
    objects.forEach(entry => {
      if (!entry.object) return;

      entry.object.traverse((child) => {
        if (child.isMesh && child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach(mat => {
            mat.clippingPlanes = this.clippingPlanes.length > 0 ? this.clippingPlanes : null;
            mat.clipIntersection = useIntersection;
            mat.needsUpdate = true;
          });
        }
      });
    });
  }

  /**
   * Clear all clipping planes from meshes
   */
  clearClippingPlanes() {
    this.clippingPlanes = [];

    // Clear bounded clipper
    if (this.boundedClipper) {
      this.boundedClipper.clearCuts();
      this.boundedClipper.setEnabled(false);
    }

    const objects = this.renderer.objects || [];
    objects.forEach(entry => {
      if (!entry.object) return;

      entry.object.traverse((child) => {
        if (child.isMesh && child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach(mat => {
            mat.clippingPlanes = null;
            mat.needsUpdate = true;
          });
        }
      });
    });

    this.renderer.renderer.render(this.scene, this.camera);
  }

  /**
   * Hide the original mesh (after cut is applied)
   */
  hideOriginalMesh() {
    if (!this.originalMeshVisible) return;
    this.originalMeshVisible = false;

    const objects = this.renderer.objects || [];
    objects.forEach(entry => {
      if (entry.object && entry.id !== 'mesh-cutter') {
        entry.object.visible = false;
      }
    });

    this.renderer.renderer.render(this.scene, this.camera);
    console.log('[CuttingPlaneWidget] Original mesh hidden');
  }

  /**
   * Show the original mesh again
   */
  showOriginalMesh() {
    if (this.originalMeshVisible) return;
    this.originalMeshVisible = true;

    const objects = this.renderer.objects || [];
    objects.forEach(entry => {
      if (entry.object && entry.id !== 'mesh-cutter') {
        entry.object.visible = true;
      }
    });

    this.renderer.renderer.render(this.scene, this.camera);
    console.log('[CuttingPlaneWidget] Original mesh restored');
  }

  syncWithPlaneData() {
    console.log('[CuttingPlaneWidget] syncWithPlaneData called, planes:', this.filterManager?.meshCutterPlanes?.length || 0);

    if (!this.filterManager?.meshCutterPlanes) {
      console.log('[CuttingPlaneWidget] No planes data available');
      return;
    }

    const currentPlaneIds = new Set(this.filterManager.meshCutterPlanes.map(p => p.id));
    for (const planeId of this.planeWidgets.keys()) {
      if (!currentPlaneIds.has(planeId)) {
        this.removePlaneWidget(planeId);
      }
    }

    this.filterManager.meshCutterPlanes.forEach(plane => {
      console.log('[CuttingPlaneWidget] Processing plane:', plane.id, 'enabled:', plane.enabled);
      if (plane.enabled) {
        if (this.planeWidgets.has(plane.id)) {
          this.updatePlaneWidgetFromData(plane);
        } else {
          this.createPlaneWidget(plane);
        }
      } else {
        if (this.planeWidgets.has(plane.id)) {
          this.removePlaneWidget(plane.id);
        }
      }
    });

    console.log('[CuttingPlaneWidget] After sync, planeWidgets count:', this.planeWidgets.size);
    this.updateClippingPlanes();
  }

  createPlaneWidget(plane) {
    if (this.planeWidgets.has(plane.id)) {
      this.removePlaneWidget(plane.id);
    }

    const planeGroup = new THREE.Group();
    planeGroup.name = `CuttingPlane_${plane.id}`;
    planeGroup.userData.planeId = plane.id;

    const origin = new THREE.Vector3(plane.origin[0], plane.origin[1], plane.origin[2]);
    const normal = new THREE.Vector3(plane.normal[0], plane.normal[1], plane.normal[2]).normalize();

    // Auto-size based on mesh bounds
    let planeSize = 1.0;
    const sceneBounds = this.renderer.getSceneBounds?.();
    if (sceneBounds) {
      const size = new THREE.Vector3().subVectors(sceneBounds.max, sceneBounds.min);
      planeSize = Math.max(size.x, size.y, size.z) * 1.2;
    } else if (this.viewer.currentMesh) {
      const box = new THREE.Box3().setFromObject(this.viewer.currentMesh);
      const size = box.getSize(new THREE.Vector3());
      planeSize = Math.max(size.x, size.y, size.z) * 1.2;
    }

    this.basePlaneSize = planeSize;

    // Apply per-plane U (width) and V (height) scaling
    const scaleU = plane.scaleU !== undefined ? plane.scaleU : 1.0;
    const scaleV = plane.scaleV !== undefined ? plane.scaleV : 1.0;
    const depth = plane.depth !== undefined ? plane.depth : 0;
    const width = (plane.width || planeSize) * scaleU;
    const height = (plane.height || planeSize) * scaleV;
    const depthSize = planeSize * depth;  // Depth in world units

    // Use plane's assigned color or default
    const planeColorHex = plane.color ? new THREE.Color(plane.color).getHex() : this.settings.planeColor;

    // Create semi-transparent plane (front plane)
    const planeGeometry = new THREE.PlaneGeometry(width, height);
    const planeMaterial = new THREE.MeshBasicMaterial({
      color: planeColorHex,
      transparent: true,
      opacity: this.settings.planeOpacity,
      side: THREE.DoubleSide,
      depthWrite: false
    });

    const planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);
    planeGroup.add(planeMesh);

    // Add border with same color but more opaque
    const edgesGeometry = new THREE.EdgesGeometry(planeGeometry);
    const edgesMaterial = new THREE.LineBasicMaterial({
      color: planeColorHex,
      transparent: true,
      opacity: this.settings.borderOpacity,
      linewidth: 2
    });
    const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
    planeGroup.add(edges);

    // If depth > 0, create back plane and connecting edges to show the volume/slab
    // The slab extends BEHIND the front plane (in -Z local direction)
    let backPlaneMesh = null;
    let backEdges = null;
    let connectingLines = null;
    if (depthSize > 1.1920929e-7) {  // float32 epsilon
      // Create back plane (offset in -Z direction, behind the front plane)
      const backPlaneGeometry = new THREE.PlaneGeometry(width, height);
      const backPlaneMaterial = new THREE.MeshBasicMaterial({
        color: planeColorHex,
        transparent: true,
        opacity: this.settings.planeOpacity * 0.7,  // Slightly more transparent
        side: THREE.DoubleSide,
        depthWrite: false
      });
      backPlaneMesh = new THREE.Mesh(backPlaneGeometry, backPlaneMaterial);
      backPlaneMesh.position.z = -depthSize;  // Offset in -Z (behind the front plane)
      planeGroup.add(backPlaneMesh);

      // Back plane border
      const backEdgesGeometry = new THREE.EdgesGeometry(backPlaneGeometry);
      const backEdgesMaterial = new THREE.LineBasicMaterial({
        color: planeColorHex,
        transparent: true,
        opacity: this.settings.borderOpacity * 0.7,
        linewidth: 2
      });
      backEdges = new THREE.LineSegments(backEdgesGeometry, backEdgesMaterial);
      backEdges.position.z = -depthSize;
      planeGroup.add(backEdges);

      // Connecting lines between corners (to visualize the slab volume)
      const halfW = width / 2;
      const halfH = height / 2;
      const corners = [
        [-halfW, -halfH], [-halfW, halfH], [halfW, halfH], [halfW, -halfH]
      ];
      const linePoints = [];
      corners.forEach(([x, y]) => {
        linePoints.push(new THREE.Vector3(x, y, 0));
        linePoints.push(new THREE.Vector3(x, y, -depthSize));  // Connect to back plane in -Z
      });
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(linePoints);
      const lineMaterial = new THREE.LineBasicMaterial({
        color: planeColorHex,
        transparent: true,
        opacity: this.settings.borderOpacity * 0.5
      });
      connectingLines = new THREE.LineSegments(lineGeometry, lineMaterial);
      planeGroup.add(connectingLines);
    }

    // Apply position and orientation
    planeGroup.position.copy(origin);
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
    planeGroup.setRotationFromQuaternion(quaternion);

    this.planeWidgets.set(plane.id, {
      group: planeGroup,
      planeMesh,
      edges,
      edgesMaterial,
      backPlaneMesh,
      backEdges,
      connectingLines,
      width,
      height,
      depth: depthSize,
      // Store scale ratios for dimension change detection during sync
      scaleU: scaleU,
      scaleV: scaleV,
      depthRatio: depth,
      visible: true,
      clippingActive: true,  // Whether this plane clips the mesh
      clippingPlane: null,
      color: planeColorHex
    });

    this.scene.add(planeGroup);
    this.renderer.renderer.render(this.scene, this.camera);

    console.log(`[CuttingPlaneWidget] Created widget for plane ${plane.id}, depth=${depthSize}`);
  }

  updatePlaneWidgetFromData(plane) {
    const widget = this.planeWidgets.get(plane.id);
    if (!widget) return;

    // Check if dimensions changed (scaleU, scaleV, depth)
    // If so, we need to rebuild the widget to get the new geometry
    const planeScaleU = plane.scaleU !== undefined ? plane.scaleU : 1.0;
    const planeScaleV = plane.scaleV !== undefined ? plane.scaleV : 1.0;
    const planeDepth = plane.depth !== undefined ? plane.depth : 0;

    // Compare with stored scale values (stored as ratios when widget was created)
    const storedScaleU = widget.scaleU !== undefined ? widget.scaleU : 1.0;
    const storedScaleV = widget.scaleV !== undefined ? widget.scaleV : 1.0;
    const storedDepthRatio = widget.depthRatio !== undefined ? widget.depthRatio : 0;

    const dimensionsChanged =
      Math.abs(planeScaleU - storedScaleU) > 1.1920929e-7 ||  // float32 epsilon
      Math.abs(planeScaleV - storedScaleV) > 1.1920929e-7 ||  // float32 epsilon
      Math.abs(planeDepth - storedDepthRatio) > 1.1920929e-7;  // float32 epsilon

    if (dimensionsChanged) {
      console.log(`[CuttingPlaneWidget] Dimensions changed for ${plane.id}, rebuilding. U: ${storedScaleU}->${planeScaleU}, V: ${storedScaleV}->${planeScaleV}, D: ${storedDepthRatio}->${planeDepth}`);
      this.rebuildPlaneWidget(plane.id);
      return;
    }

    // Just update position and rotation
    const origin = new THREE.Vector3(plane.origin[0], plane.origin[1], plane.origin[2]);
    const normal = new THREE.Vector3(plane.normal[0], plane.normal[1], plane.normal[2]).normalize();

    widget.group.position.copy(origin);
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);
    widget.group.setRotationFromQuaternion(quaternion);

    this.renderer.renderer.render(this.scene, this.camera);
  }

  /**
   * Toggle visibility of a plane widget (preview only, not the clipping)
   */
  setPlaneWidgetVisible(planeId, visible) {
    const widget = this.planeWidgets.get(planeId);
    if (!widget) return;

    widget.visible = visible;
    widget.group.visible = visible;

    // Hide/show the gizmo if this plane is currently selected
    if (this.activePlaneId === planeId && this.transformControls) {
      this.transformControls.visible = visible;
      this.transformControls.enabled = visible;
    }

    this.updateClippingPlanes();
    this.filterManager?.renderCuttingPlanesList();
    this.renderer.renderer.render(this.scene, this.camera);
  }

  /**
   * Toggle plane widget visibility
   */
  togglePlaneWidgetVisibility(planeId) {
    const widget = this.planeWidgets.get(planeId);
    if (!widget) return;

    this.setPlaneWidgetVisible(planeId, !widget.visible);
  }

  /**
   * Check if a plane widget is visible
   */
  isPlaneWidgetVisible(planeId) {
    const widget = this.planeWidgets.get(planeId);
    return widget ? widget.visible : true;
  }

  /**
   * Toggle clipping effect for a plane (whether mesh is clipped by this plane)
   */
  togglePlaneClipping(planeId) {
    const widget = this.planeWidgets.get(planeId);
    if (!widget) return;

    widget.clippingActive = !widget.clippingActive;
    this.updateClippingPlanes();
    this.filterManager?.renderCuttingPlanesList();
  }

  /**
   * Check if a plane's clipping effect is active
   */
  isPlaneClippingActive(planeId) {
    const widget = this.planeWidgets.get(planeId);
    return widget ? widget.clippingActive : true;
  }

  removePlaneWidget(planeId) {
    const widget = this.planeWidgets.get(planeId);
    if (!widget) return;

    if (this.activePlaneId === planeId) {
      this.deselectPlane();
    }

    this.scene.remove(widget.group);
    widget.group.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(mat => mat.dispose());
        } else {
          child.material.dispose();
        }
      }
    });

    this.planeWidgets.delete(planeId);
    this.updateClippingPlanes();
    this.renderer.renderer.render(this.scene, this.camera);

    console.log(`[CuttingPlaneWidget] Removed widget for plane ${planeId}`);
  }

  removeAllPlaneWidgets() {
    this.removeTransformControls();

    for (const planeId of this.planeWidgets.keys()) {
      const widget = this.planeWidgets.get(planeId);
      if (widget) {
        this.scene.remove(widget.group);
        widget.group.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(mat => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
    }
    this.planeWidgets.clear();
  }

  selectPlane(planeId) {
    if (this.activePlaneId === planeId) return;

    if (this.activePlaneId) {
      this.deselectPlane();
    }

    const widget = this.planeWidgets.get(planeId);
    if (!widget) return;

    this.activePlaneId = planeId;

    // Highlight with white/bright border when selected
    widget.edgesMaterial.color.setHex(0xFFFFFF);
    widget.edgesMaterial.opacity = 1.0;

    this.createTransformControls(widget.group);

    // Sync sliders and inputs with selected plane's scale values
    const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === planeId);
    if (plane) {
      // Get values as percentages (support decimals)
      const scaleUPercent = (plane.scaleU !== undefined ? plane.scaleU : 1.0) * 100;
      const scaleVPercent = (plane.scaleV !== undefined ? plane.scaleV : 1.0) * 100;
      const depthPercent = (plane.depth !== undefined ? plane.depth : 0) * 100;

      const scaleUSlider = document.getElementById('cutPlaneScaleU');
      const scaleUInput = document.getElementById('cutPlaneScaleUValue');
      const scaleVSlider = document.getElementById('cutPlaneScaleV');
      const scaleVInput = document.getElementById('cutPlaneScaleVValue');
      const depthSlider = document.getElementById('cutPlaneDepth');
      const depthInput = document.getElementById('cutPlaneDepthValue');
      const activeElement = document.activeElement;

      // U slider: 0-100 maps to 0-200%, so divide by 2 for slider position
      if (scaleUSlider) scaleUSlider.value = Math.min(100, scaleUPercent / 2);
      // Don't overwrite input if user is currently typing in it
      if (scaleUInput && activeElement !== scaleUInput) scaleUInput.value = scaleUPercent;

      // V slider: 0-100 maps to 0-200%, so divide by 2 for slider position
      if (scaleVSlider) scaleVSlider.value = Math.min(100, scaleVPercent / 2);
      // Don't overwrite input if user is currently typing in it
      if (scaleVInput && activeElement !== scaleVInput) scaleVInput.value = scaleVPercent;

      // Depth slider: 0-100 maps directly to 0-100%
      if (depthSlider) depthSlider.value = Math.min(100, depthPercent);
      // Don't overwrite input if user is currently typing in it
      if (depthInput && activeElement !== depthInput) depthInput.value = depthPercent;
    }

    console.log(`[CuttingPlaneWidget] Selected plane ${planeId}`);
    this.renderer.renderer.render(this.scene, this.camera);
  }

  deselectPlane() {
    if (!this.activePlaneId) return;

    const widget = this.planeWidgets.get(this.activePlaneId);
    if (widget) {
      // Restore original plane color
      widget.edgesMaterial.color.setHex(widget.color || this.settings.borderColor);
      widget.edgesMaterial.opacity = this.settings.borderOpacity;
    }

    this.removeTransformControls();
    this.activePlaneId = null;

    this.renderer.renderer.render(this.scene, this.camera);
  }

  createTransformControls(targetGroup) {
    if (!THREE.TransformControls) {
      console.error('[CuttingPlaneWidget] TransformControls not available! Check if script is loaded.');
      return;
    }

    this.removeTransformControls();

    try {
      this.transformControls = new THREE.TransformControls(
        this.camera,
        this.renderer.renderer.domElement
      );

      this.transformControls.setMode('translate');
      this.transformControls.setSpace('local');
      this.transformControls.setSize(1.2);  // Make gizmo slightly larger for visibility
      this.transformControls.attach(targetGroup);
      this.scene.add(this.transformControls);

      console.log('[CuttingPlaneWidget] TransformControls created and attached');

      this.transformControls.addEventListener('dragging-changed', (event) => {
        this.controls.enabled = !event.value;
      });

      this.transformControls.addEventListener('change', () => {
        this.onTransformChange();
      });
    } catch (error) {
      console.error('[CuttingPlaneWidget] Error creating TransformControls:', error);
    }

    this.keydownHandler = (event) => {
      if (!this.enabled || !this.activePlaneId) return;

      // Don't intercept keys when user is typing in an input field
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'g':
          event.preventDefault();
          event.stopPropagation();
          this.transformControls.setMode('translate');
          this.transformControls.setSpace('local');
          Utils.notify('Cutting plane: Translate mode', 'info', 1000);
          break;
        case 'r':
          event.preventDefault();
          event.stopPropagation();
          this.transformControls.setMode('rotate');
          this.transformControls.setSpace('local');  // Local space for intuitive rotation around plane axes
          Utils.notify('Cutting plane: Rotate mode', 'info', 1000);
          break;
        case 'escape':
          event.preventDefault();
          event.stopPropagation();
          this.deselectPlane();
          break;
      }
    };

    // Use capture phase to intercept events before other handlers
    window.addEventListener('keydown', this.keydownHandler, true);

    // Render to show the transform controls
    this.renderer.renderer.render(this.scene, this.camera);
  }

  onTransformChange() {
    if (!this.enabled || !this.activePlaneId) return;

    const widget = this.planeWidgets.get(this.activePlaneId);
    if (!widget) return;

    const group = widget.group;
    const origin = group.position;

    const normalLocal = new THREE.Vector3(0, 0, 1);
    normalLocal.applyQuaternion(group.quaternion);
    normalLocal.normalize();

    // Update the plane data in filterManager
    const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === this.activePlaneId);
    if (plane) {
      plane.origin = [origin.x, origin.y, origin.z];
      plane.normal = [normalLocal.x, normalLocal.y, normalLocal.z];
    }

    // Update clipping preview in real-time
    this.updateClippingPlanes();

    this.renderer.renderer.render(this.scene, this.camera);
  }

  removeTransformControls() {
    if (this.transformControls) {
      this.transformControls.enabled = false;
      this.transformControls.visible = false;
      this.transformControls.detach();
      this.scene.remove(this.transformControls);
      this.transformControls.dispose();
      this.transformControls = null;
    }

    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler, true);
      this.keydownHandler = null;
    }
  }

  onPlaneAdded(planeId) {
    if (!this.enabled) return;

    const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === planeId);
    if (plane) {
      this.createPlaneWidget(plane);
      this.selectPlane(planeId);
      this.updateClippingPlanes();
    }
  }

  onPlaneRemoved(planeId) {
    this.removePlaneWidget(planeId);
    this.updateClippingPlanes();
  }

  onPlaneToggled(planeId) {
    const plane = this.filterManager?.meshCutterPlanes?.find(p => p.id === planeId);
    if (!plane) return;

    if (plane.enabled) {
      if (!this.planeWidgets.has(planeId)) {
        this.createPlaneWidget(plane);
      }
    } else {
      this.removePlaneWidget(planeId);
    }
    this.updateClippingPlanes();
  }

  onPlanesCleared() {
    this.removeAllPlaneWidgets();
    this.clearClippingPlanes();
  }

  /**
   * Called after cut is applied - hide original mesh
   */
  onCutApplied() {
    this.hideOriginalMesh();
    // Clear clipping planes since we now have the actual cut mesh
    this.clearClippingPlanes();
  }

  /**
   * Called when cut is cleared - show original mesh
   */
  onCutCleared() {
    this.showOriginalMesh();
    if (this.enabled) {
      this.updateClippingPlanes();
    }
  }

  cleanup() {
    this.disable();

    // Remove bounded clipper from all materials
    if (this.boundedClipper) {
      this.boundedClipper.removeFromAllMaterials();
      this.boundedClipper = null;
    }
  }
}
